import type { ReactNode } from 'react';
import EmptyState from './EmptyState';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** cell renderer */
  render: (row: T) => ReactNode;
  /** extra style for both th + td (e.g. width, font-family) */
  cellStyle?: React.CSSProperties;
  className?: string;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
}

/* ctAS §6.3 — the default data display: zebra table-in-a-card with
   a muted uppercase header and full-width clickable rows. */
export default function DataTable<T>({ columns, rows, rowKey, onRowClick, empty }: Props<T>) {
  return (
    <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
      {rows.length === 0 ? (
        <EmptyState>{empty ?? 'Nothing here yet.'}</EmptyState>
      ) : (
        <table className="scpa-table">
          <thead>
            <tr>
              {columns.map(c => (
                <th key={c.key} className="scpa-th" style={c.cellStyle}>{c.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? 'scpa-row' : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map(c => (
                  <td key={c.key} className={`scpa-td ${c.className ?? ''}`} style={c.cellStyle}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
